import { DateTime, Interval } from 'luxon';
import { useCallback, useEffect, useState } from 'react';

const TOTAL_TIME_SEC = 30 * 60; // 30 minutes
const WARNING_TIME_SEC = 10 * 60; // 10 minutes
const LOCAL_STORAGE_KEY = 'lastActiveTime';

const getLastActiveTime: () => DateTime = () => {
  const millisString = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (millisString === null) {
    return DateTime.now();
  }
  return DateTime.fromMillis(parseInt(millisString));
};
const resetLastActiveTime: () => void = () => {
  const nowMillis = DateTime.now().toMillis();
  localStorage.setItem(LOCAL_STORAGE_KEY, `${nowMillis}`);
};

export const useInactivityTimer = (timeoutCallback: () => void) => {
  const [isWarning, setIsWarning] = useState<boolean>(false);
  const [timeLeftSec, setTimeLeftSec] = useState<number>(TOTAL_TIME_SEC);
  const [isTimeoutReached, setIsTimeoutReached] = useState<boolean>(false);

  const resetTimer = () => {
    resetLastActiveTime();
    setIsWarning(false);
    setTimeLeftSec(TOTAL_TIME_SEC);
    setIsTimeoutReached(false);
  };

  const handleTimeout = useCallback(() => {
    if (!isTimeoutReached) {
      setIsTimeoutReached(true);
      timeoutCallback();
    }
  }, [isTimeoutReached]);

  //reset time on load
  useEffect(() => {
    resetLastActiveTime();
  }, []);

  // check timer once a second and update as needed
  useEffect(() => {
    const timer = setInterval(() => {
      const elapsedInterval = Interval.fromDateTimes(
        getLastActiveTime(),
        DateTime.now()
      );
      const secondsRemaining =
        TOTAL_TIME_SEC - elapsedInterval.length('seconds');
      setTimeLeftSec(secondsRemaining);
      setIsWarning(secondsRemaining < WARNING_TIME_SEC);
      if (secondsRemaining < 0) {
        handleTimeout();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [handleTimeout]);

  // set window event listeners to reset local storage value
  useEffect(() => {
    window.addEventListener('keydown', resetTimer);
    window.addEventListener('mousedown', resetTimer);
    window.addEventListener('touchstart', resetTimer);
    window.addEventListener('click', resetTimer);
    return () => {
      window.removeEventListener('keydown', resetTimer);
      window.removeEventListener('mousedown', resetTimer);
      window.removeEventListener('touchstart', resetTimer);
      window.removeEventListener('click', resetTimer);
    };
  }, []);

  return { isWarning, timeLeftSec, resetTimer };
};
